import React, { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Form } from '../../Form';
import { CustomErrorTypeMessages } from '../../partials/StatusMessages';
import { SonForm } from '../../types/SonForm';
import { useForm } from '../../useForm';
import { SubmitButton } from '../../elements/SubmitButton/SubmitButton';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { RequiredHelpPhrase } from '../../partials/RequiredHelpPhrase/RequiredHelpPhrase';
import { Row } from '../../grid/Row/Row';
import { Textarea } from '../../elements/Textarea/Textarea';
import { Input } from '../../elements/Input/Input';
import { TextAreaParam } from '../../types/TextParam';
import { DateParam } from '../../types/DateParam';
import { PublishFormSwitch, PublishFormSwitchParameter } from './partials/PublishFormSwitch';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import {
  ListBoxModifier,
  PersonPreview,
  UserComboBoxForForm2,
} from '../../../../components/molecules/Form/components/ComboBox';
import { ComboBoxParam } from '../../types/ComboBoxParam';
// eslint-disable-next-line max-len
import useUserMultiSelectComboBox from '../../../../components/molecules/Form/components/ComboBox/User/useUserMultiSelectComboBox';

import {
  FormsComboboxMultiSelectList,
// eslint-disable-next-line max-len
} from '../../../../components/molecules/Form/components/FormsComboboxMultiSelect/partials/FormsComboboxMultiSelectList';

import {
  FormsComboboxMultiSelectListElement,
// eslint-disable-next-line max-len
} from '../../../../components/molecules/Form/components/FormsComboboxMultiSelect/partials/FormsComboboxMultiSelectListElement';
import { PersonInformationProps } from '../../../../components/molecules/PersonInformation/PersonInformation';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';

export type PublishSonForm = SonForm<
  | PublishFormSwitchParameter
  | TextAreaParam<'changeComment'>
  | TextAreaParam<'publicationProposalComment'>
  | DateParam<'validUntil'>
  | DateParam<'scheduledDate'>
  | DateParam<'archivedDate'>
  | ComboBoxParam<'persons'>
>;

type PublishForm = CustomErrorTypeMessages & {
  form: PublishSonForm;
  onSuccess?: (response: AxiosResponse) => void;
};

export function PublishForm({ form, onSuccess, customErrorTypeMessages }: PublishForm) {
  const { t } = useSonTranslation('PageManagement');
  const { formProps, fetchState, params } = useForm({ form, onSuccess });

  const [importantChangeChecked, setImportantChangeChecked] = useState(false);
  const [triggerTimeControlledPublicationChecked, setTriggerTimeControlledPublicationChecked] = useState(false);

  const pagePublishNonAreaSubPages = params.findByName('pagePublishNonAreaSubPages');
  const pagePublishAreaSubPages = params.findByName('pagePublishAreaSubPages');
  const importantChange = params.findByName('importantChange');
  const changeComment = params.findByName('changeComment');
  const validUntil = params.findByName('validUntil');
  const triggerTimeControlledPublication = params.findByName('triggerTimeControlledPublication');
  const scheduledDate = params.findByName('scheduledDate');
  const persons = params.findByName('persons');
  const publicationProposalComment = params.findByName('publicationProposalComment');

  // to be refactored: because of conditionality => put this together with <UserComboBoxForForm2> in a component?
  const { getComboBoxProps, selectedItems, unselectItem } = useUserMultiSelectComboBox({
    suggestUrl: persons?.comboBoxUrl.url || '',
  });

  const { onSelection, ...comboboxProps } = getComboBoxProps();

  function deleteDuplicateSelections(personItems: Array<PersonInformationProps>) {
    const uniqueStrings = new Set(personItems.map((personItem: PersonInformationProps) => JSON.stringify(personItem)));
    return Array.from(uniqueStrings).map(string => JSON.parse(string));
  }

  const selectedUniqueUsers = deleteDuplicateSelections(selectedItems);

  const formId = useHtmlValidId();

  // Submit button and interaction messages
  const submitButtonText = persons ? t('views.propose-publication.form.submit') : t('views.publish.form.submit');
  const messageSuccess = () => {
    // if proposed publication, use proposed publication text on time controlled publication as well
    if (triggerTimeControlledPublicationChecked) {
      if (persons) {
        return t(`views.propose-publication.form.status.success`);
      }
      return t('views.publish.form.status.successTimeControlledPublication');
    }
    if (persons) {
      return t(`views.propose-publication.form.status.success`);
    }
    return t(`views.publish.form.status.success`);
  };
  // different forwarding messages on time controlled publication and  proposed publication
  const messageForward = () => {
    if (triggerTimeControlledPublicationChecked) {
      return t('views.publish.form.status.successForwardTimeControlled');
    }
    if (persons) {
      return t(`views.propose-publication.form.status.successForward`);
    }
    return t(`views.publish.form.status.successForward`);
  };
  const messageSending = () => {
    if (triggerTimeControlledPublicationChecked) {
      return t('views.publish.form.status.sendingTimeControlledPublication');
    }
    if (persons) {
      return t(`views.propose-publication.form.status.sending`);
    }
    return t(`views.publish.form.status.sending`);
  };
  const messageFail = persons ? t(`views.propose-publication.form.status.fail`) : t(`views.publish.form.status.fail`);

  /**
   * When this form is opened with preselected data (proposed publication), those states have to be set.
   */
  useEffect(() => {
    if (importantChange?.checked === true) {
      setImportantChangeChecked(true);
    }
    if (triggerTimeControlledPublication?.checked === true) {
      setTriggerTimeControlledPublicationChecked(true);
    }
  }, [importantChange, triggerTimeControlledPublication]);

  return (
    <Form {...formProps} id={`page-publish-form-${formId}`}>
      {params.some(param => param.required) ? <RequiredHelpPhrase /> : null}
      {pagePublishNonAreaSubPages ? <PublishFormSwitch parameter={pagePublishNonAreaSubPages} /> : null}
      {pagePublishAreaSubPages ? <PublishFormSwitch parameter={pagePublishAreaSubPages} /> : null}
      {importantChange
        ? (
            <PublishFormSwitch
              parameter={importantChange}
              onClick={() => setImportantChangeChecked(!importantChangeChecked)}
            />
          )
        : null}
      {changeComment && importantChangeChecked
        ? (
            <Row>
              <Textarea label={t(`views.publish.form.inputs.${changeComment.name}.label`)} param={changeComment} />
            </Row>
          )
        : null}

      {validUntil
        ? (
            <Row>
              <Input label={t(`views.publish.form.inputs.${validUntil.name}.label`)} param={validUntil} />
            </Row>
          )
        : null}
      {triggerTimeControlledPublication
        ? (
            <PublishFormSwitch
              parameter={triggerTimeControlledPublication}
              onClick={() => setTriggerTimeControlledPublicationChecked(!triggerTimeControlledPublicationChecked)}
            />
          )
        : null}
      {scheduledDate && triggerTimeControlledPublicationChecked
        ? (
            <Row>
              <Input
                label={t(`views.publish.form.inputs.${scheduledDate.name}.label`)}
                param={scheduledDate}
                type="datetime-local"
              />
            </Row>
          )
        : null}
      {persons
        ? (
            <>
              <Row modifier="block">
                <UserComboBoxForForm2
                  labelTitle={t(`views.propose-publication.form.inputs.${persons.name}.label`)}
                  suggestUrl={persons.comboBoxUrl.url}
                  required={persons.required}
                  modifierListBox={ListBoxModifier.RELATIVE}
                  validationMessage={
                    !selectedItems || selectedItems.length === 0
                      ? t(`views.propose-publication.form.inputs.${persons.name}.validationMessage`)
                      : ''
                  }
                  onSelection={(items) => {
                    sendLiveRegionMessage(
                      t(
                        `views.propose-publication.form.inputs.${persons.name}.selectedPerson`,
                        // @ts-expect-error out typing seems to be broken
                        { name: items?.selectedItem?.profileName },
                      ));
                    onSelection(items);
                  }}
                  {...comboboxProps}
                />
              </Row>
              {selectedUniqueUsers && selectedUniqueUsers.length > 0 && (
                <>
                  <h3 className="invisible" id={`publish-form-selected-users-list-${formId}`}>
                    {t(`views.propose-publication.form.inputs.${persons.name}.headline`)}
                  </h3>
                  <FormsComboboxMultiSelectList labelId={`publish-form-selected-users-list-${formId}`}>
                    {selectedUniqueUsers.map((item: any) => (
                      <FormsComboboxMultiSelectListElement key={item.id + item.pageLink}>
                        <input type="hidden" value={item.id} name={persons.name} />
                        <PersonPreview
                          id={item.id}
                          emailLink={item.emailLink}
                          orgUnit={item.orgUnit}
                          profileImageUrl={item.profileImageUrl}
                          profileLink={item.pageLink}
                          phoneLink={item.phoneLink}
                          reset={() => {
                            unselectItem(item.id);
                            sendLiveRegionMessage(t(
                              `views.propose-publication.form.inputs.${persons.name}.removedPerson`,
                              { name: item?.profileName },
                            ));
                          }}
                        />
                      </FormsComboboxMultiSelectListElement>
                    ))}
                  </FormsComboboxMultiSelectList>
                </>
              )}
            </>
          )
        : null}
      {publicationProposalComment
        ? (
            <Row>
              <Textarea
                label={t(`views.propose-publication.form.inputs.${publicationProposalComment.name}.label`)}
                param={publicationProposalComment}
              />
            </Row>
          )
        : null}
      <SubmitButton
        buttonLabel={submitButtonText}
        messageSuccess={messageSuccess()}
        messageForward={messageForward()}
        messageSending={messageSending()}
        messageFail={messageFail}
        customErrorTypeMessages={customErrorTypeMessages}
        fetchState={fetchState}
        disabled={fetchState.type !== 'initial'}
      />
    </Form>
  );
}
